import type { SmNode, NodeType } from './types.js';
import { nodesSvg, mainPanel, statusBar, semiManualBtn, refitBtn } from './dom.js';
import { ZoomPan } from './zoompan.js';

/** Whether semi-manual node placement mode is active. */
export let semiManual: boolean = false;
/** Ordered list of placed nodes for semi-manual fitting. */
export let smNodes: SmNode[] = [];

/** Base radius (in image px at 1x zoom) for rendering node circles. */
const NODE_RADIUS: number = 6;
/** Hit-test radius (in image px at 1x zoom) for clicking existing nodes. */
const NODE_HIT_RADIUS: number = 12;

/** Activate semi-manual mode: show bar, enable node placement. */
export function enterSemiManual(): void {
  semiManual = true;
  smNodes = [];
  semiManualBtn.checked = true;
  refitBtn.textContent = 'Fit';
  refitBtn.classList.remove('bg-amber-600', 'hover:bg-amber-500');
  refitBtn.classList.add('bg-emerald-600', 'hover:bg-emerald-500');
  refitBtn.disabled = true;
  mainPanel.style.cursor = 'crosshair';
  renderNodes();
  statusBar.textContent = 'Click to place nodes (smooth), click node: free then remove';
}

/** Deactivate semi-manual mode: hide bar, clear nodes. */
export function exitSemiManual(): void {
  semiManual = false;
  smNodes = [];
  semiManualBtn.checked = false;
  refitBtn.textContent = 'Refit';
  refitBtn.classList.remove('bg-emerald-600', 'hover:bg-emerald-500');
  refitBtn.classList.add('bg-amber-600', 'hover:bg-amber-500');
  refitBtn.disabled = true;
  nodesSvg.style.display = 'none';
  mainPanel.style.cursor = 'crosshair';
}

/**
 * Handle a click in semi-manual mode.
 * - Click near existing node: toggle smooth→free, or free→remove.
 * - Click empty space: place a new smooth node.
 */
export function handleSemiManualClick(ix: number, iy: number, zp: ZoomPan): void {
  const hitIdx: number = smNodes.findIndex((n: SmNode): boolean => {
    const dx: number = n.x - ix, dy: number = n.y - iy;
    return dx * dx + dy * dy < NODE_HIT_RADIUS * NODE_HIT_RADIUS / (zp.zoom * zp.zoom);
  });

  if (hitIdx >= 0) {
    const node: SmNode = smNodes[hitIdx];
    if (node.type === 'smooth') {
      node.type = 'free' as NodeType;
    } else {
      smNodes.splice(hitIdx, 1);
    }
  } else {
    smNodes.push({ x: ix, y: iy, type: 'smooth' as NodeType });
  }
  renderNodes();
  refitBtn.disabled = smNodes.length < 3;
  const counts: Record<string, number> = smNodes.reduce<Record<string, number>>(
    (a: Record<string, number>, n: SmNode): Record<string, number> => {
      a[n.type] = (a[n.type] || 0) + 1;
      return a;
    },
    { smooth: 0, free: 0 },
  );
  statusBar.textContent = `${smNodes.length} node${smNodes.length !== 1 ? 's' : ''} (${counts.smooth} smooth, ${counts.free} free)`;
}

/** Render node markers to the nodesSvg overlay (no zoom scaling). */
function renderNodes(): void {
  if (smNodes.length === 0) {
    nodesSvg.style.display = 'none';
    return;
  }
  nodesSvg.style.display = 'block';
  let html: string = '';
  const nr: number = NODE_RADIUS;
  const sw: number = 1.5;
  for (const n of smNodes) {
    if (n.type === 'smooth') {
      html += `<circle cx="${n.x.toFixed(1)}" cy="${n.y.toFixed(1)}" r="${nr}" fill="#60a5fa" stroke="white" stroke-width="${sw}" class="sm-node"/>`;
    } else {
      const s: number = nr * 1.2;
      const cx: number = n.x, cy: number = n.y;
      const pts: string = [
        [cx, cy - s].join(','),
        [cx - s * 0.866, cy + s * 0.5].join(','),
        [cx + s * 0.866, cy + s * 0.5].join(','),
      ].join(' ');
      html += `<polygon points="${pts}" fill="#f59e0b" stroke="white" stroke-width="${sw}" class="sm-node"/>`;
    }
  }
  nodesSvg.innerHTML = html;
}

/** Render nodes with zoom-aware sizing, setting viewBox to image dimensions. */
export function renderNodesForZoom(imgW: number, imgH: number, zp: ZoomPan): void {
  if (smNodes.length === 0) { nodesSvg.style.display = 'none'; return; }
  nodesSvg.style.display = 'block';
  nodesSvg.setAttribute('viewBox', `0 0 ${imgW} ${imgH}`);
  nodesSvg.setAttribute('width', String(imgW));
  nodesSvg.setAttribute('height', String(imgH));
  let html: string = '';
  const nr: number = NODE_RADIUS / zp.zoom;
  const sw: number = 1.5 / zp.zoom;
  for (const n of smNodes) {
    if (n.type === 'smooth') {
      html += `<circle cx="${n.x.toFixed(1)}" cy="${n.y.toFixed(1)}" r="${nr}" fill="#60a5fa" stroke="white" stroke-width="${sw}" class="sm-node"/>`;
    } else {
      const s: number = nr * 1.2;
      const cx: number = n.x, cy: number = n.y;
      const pts: string = [
        [cx, cy - s].join(','),
        [cx - s * 0.866, cy + s * 0.5].join(','),
        [cx + s * 0.866, cy + s * 0.5].join(','),
      ].join(' ');
      html += `<polygon points="${pts}" fill="#f59e0b" stroke="white" stroke-width="${sw}" class="sm-node"/>`;
    }
  }
  nodesSvg.innerHTML = html;
}
